import { Button, Card, message, Spin, Table, Typography } from "antd";
import { Pie } from "@ant-design/plots";
import { formatNumber } from "@/utils/formatCurrency";
import type { DashboardData } from "@/types/dashboard";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GetDashboard } from "@/services/dashboard.service";
import { safeFormatyyyyMMddDateTime } from "@/utils/formatDate";
import { ROUTES } from "@/constants/routes";
import { renderNameCell } from "@/features/admin/hook/renderNameCell";

const { Title, Text } = Typography;

const getTwoToneColor = (key: string, first: string) =>
  key === first ? "#0081B3" : "#003D54";

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);

        const response = await GetDashboard();

        setDashboard(response.data);
      } catch (error) {
        message.error(
          error instanceof Error
            ? error.message
            : "Gagal mendapatkan data dashboard",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const pieTotalData = dashboard?.categories ?? [];
  const pieGenderData = dashboard?.genders ?? [];

  const genderConfig = {
    data: pieGenderData,
    angleField: "count",
    colorField: "label",
    legend: false,

    scale: {
      color: {
        range: ["#003D54", "#0081B3"],
      },
    },
  };

  const totalConfig = {
    data: pieTotalData,
    angleField: "count",
    colorField: "label",
    innerRadius: 0.6,
    legend: false,

    scale: {
      color: {
        range: ["#003D54", "#0081B3"],
      },
    },

    annotations: [
      {
        type: "text",
        style: {
          text: formatNumber(dashboard?.statisticsTotal?.total ?? 0),
          x: "50%",
          y: "45%",
          textAlign: "center",
          fontSize: 20,
          fontWeight: 700,
        },
      },
      {
        type: "text",
        style: {
          text: dashboard?.statisticsTotal?.label ?? "",
          x: "50%",
          y: "56%",
          textAlign: "center",
          fontSize: 14,
          fontWeight: 500,
        },
      },
    ],
  };

  const columnNews = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value: unknown) => renderNameCell(value),
    },
    {
      title: "Kampung",
      dataIndex: "villageName",
      key: "villageName",
    },
    {
      title: "Tarikh Berdaftar",
      dataIndex: "submittedOn",
      key: "submittedOn",
      render: (value: Date | string) => safeFormatyyyyMMddDateTime(value),
    },
  ];

  const columnTotal = [
    {
      title: "Kampung",
      dataIndex: "villageName",
      key: "villageName",
    },
    {
      title: "Jumlah diterima",
      dataIndex: "approveTotal",
      key: "approveTotal",
      render: (value: number) => formatNumber(value),
    },
    {
      title: "Jumlah belum diproses",
      dataIndex: "pendingTotal",
      key: "pendingTotal",
      render: (value: number) => formatNumber(value),
    },
    {
      title: "Jumlah yang ditolak",
      dataIndex: "rejectTotal",
      key: "rejectTotal",
      render: (value: number) => formatNumber(value),
    },
  ];

  return (
    <section className="px-4 sm:px-10 py-6 h-[calc(100dvh-64px)]">
      <div className="grid md:grid-cols-[1.2fr_2fr] gap-6 h-full">
        <Card
          variant="borderless"
          className="shadow-xl! h-full!"
          styles={{
            body: {
              height: "100%",
            },
          }}
        >
          <div className="flex h-full flex-col gap-6">
            <Title
              level={4}
              className="text-xl sm:text-2xl! text-primary! font-bold!"
            >
              Statistik
            </Title>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center h-full">
              {loading ? (
                <div className="h-80 flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : (
                <Pie height={320} {...totalConfig} />
              )}

              <div className="space-y-2">
                {pieTotalData.map((item) => {
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-6 p-4"
                    >
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{
                          backgroundColor: getTwoToneColor(
                            item.label,
                            "Bukan Mastautin",
                          ),
                        }}
                      ></div>
                      <div className="flex-1 flex flex-col gap-2">
                        <Text strong className="text-4xl!">
                          {formatNumber(item.count)}
                        </Text>
                        <Text className="font-medium! text-sm!">
                          {item.label}
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center h-full">
              {loading ? (
                <div className="h-80 flex items-center justify-center">
                  <Spin size="large" />
                </div>
              ) : (
                <Pie height={320} {...genderConfig} />
              )}

              <div className="space-y-2">
                {pieGenderData.map((item) => {
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-6 p-4"
                    >
                      <div
                        className="w-6 h-6 rounded-full"
                        style={{
                          backgroundColor: getTwoToneColor(
                            item.label,
                            "Perempuan",
                          ),
                        }}
                      ></div>
                      <div className="flex-1 flex flex-col gap-2">
                        <Text strong className="text-4xl!">
                          {formatNumber(item.count)}
                        </Text>
                        <Text className="font-medium! text-sm!">
                          {item.label}
                        </Text>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        <Card variant="borderless" className="shadow-xl! h-full!">
          <div className="flex flex-col gap-10">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <Title
                    level={4}
                    className="mb-0! text-primary! font-bold! text-lg! sm:text-2xl!"
                  >
                    Permohonan terbaharu yang belum diproses
                  </Title>
                  <div className="bg-[#FECD39] min-w-24 text-center text-xl sm:text-2xl rounded-full font-bold px-2 py-1">
                    {formatNumber(dashboard?.pendingTotal ?? 0)}
                  </div>
                </div>

                <Button
                  htmlType="button"
                  variant="solid"
                  size="large"
                  className="min-w-48"
                  onClick={() => navigate(ROUTES.adminNotApplication)}
                >
                  Lihat Semua
                </Button>
              </div>
              <div className="mt-6 rounded-2xl bg-white shadow-lg">
                <Table
                  size="small"
                  columns={columnNews}
                  dataSource={dashboard?.recentPendingList ?? []}
                  pagination={false}
                  loading={loading}
                  className="c-table"
                />
              </div>
            </div>

            <div>
              <Title
                level={4}
                className="mb-0! text-primary! font-bold! text-lg! sm:text-2xl!"
              >
                Jumlah pemohon mengikut kampung
              </Title>
              <div className="mt-6 rounded-2xl bg-white shadow-lg">
                <Table
                  size="small"
                  columns={columnTotal}
                  dataSource={dashboard?.villageSummaryList ?? []}
                  pagination={false}
                  loading={loading}
                  className="c-table"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
